/**
 * Adapts Mermaid to the render loop's `RenderFn` — SPEC.md §2, §3, §12 M1.
 *
 * `mermaid` is only ever imported dynamically (rule 5): the shell must paint
 * before ~1MB of renderer arrives. `securityLevel: 'strict'` and
 * `htmlLabels: false` are ADR-governed (docs/adr/0001, docs/adr/0002), not
 * tuning knobs. `fontFamily` stays a system stack so exports stay portable
 * (SPEC.md §6 gotcha 4) — Mermaid's own text deliberately doesn't match the
 * UI font.
 *
 * M4.4 (issue #18) registers `@mermaid-js/layout-elk` alongside Mermaid
 * itself, both loaded in the same dynamic-import wave, so a diagram that
 * opens with `layout: elk` in frontmatter works on its very first render —
 * no separate warm-up attempt. Also dynamic (rule 5): it must not land in
 * the initial JS chunk either.
 *
 * M4.5 (issue #19) registers `@mermaid-js/mermaid-zenuml` too, but lazily —
 * unlike ELK, ZenUML is one specific diagram type (SPEC.md §4's "the one
 * catalog entry that isn't core Mermaid"), so loading it for every render
 * would tax the 26 other types for a plugin most sessions never touch.
 * `ensureZenuml()` mirrors `getMermaid()`'s own caching-promise pattern so
 * the dynamic import and registration happen at most once per session.
 *
 * M6.6 (issue #33) adds `setMermaidConfig(diagramTheme, look)`: SPEC.md §8's
 * diagram theme and look belong to the diagram, not the app, so they're
 * global Mermaid init options, re-applied via `mermaid.initialize()`
 * whenever they actually change — mirroring this module's own
 * memoized-promise/cached-registration shape rather than adding a third
 * pattern. `render.ts`'s `RenderFn` signature stays untouched: Mermaid reads
 * its currently-initialized config on every `render()` call, so there's
 * nothing new to thread through the render loop itself.
 */
import { detectDiagramType } from './diagramTypes';
import type { RenderFn } from './render';
import { withExplicitDimensions } from './svgDimensions';
import type { Mermaid } from 'mermaid';

const ZENUML_LOADING_NOTICE = 'Loading ZenUML plugin';

// SPEC.md §8's exact 5: "Diagram theme select: default, neutral, forest,
// dark, base." Mermaid's own theme union is wider (adds neo/redux variants
// etc.) — this is the deliberately narrower catalog the app exposes.
export const DIAGRAM_THEMES = ['default', 'neutral', 'forest', 'dark', 'base'] as const;
export type DiagramTheme = (typeof DIAGRAM_THEMES)[number];

// SPEC.md §8: "Look select: classic / handDrawn." Mermaid also supports a
// third 'neo' look, not part of this app's catalog.
export const LOOKS = ['classic', 'handDrawn'] as const;
export type Look = (typeof LOOKS)[number];

// The one place a theme/look gets its display label — mirrors export.ts's
// own RASTER_FORMAT_META precedent (a format/scale only ever needs one
// entry there, not edits scattered across that file and ExportMenu.tsx).
// M6.8 (issue #35)'s command palette needs the exact same labels
// ThemeMenu.tsx/LayoutMenu.tsx already show, so all three read from here
// instead of each carrying its own copy.
export const DIAGRAM_THEME_LABELS: Record<DiagramTheme, string> = {
  default: 'Default',
  neutral: 'Neutral',
  forest: 'Forest',
  dark: 'Dark',
  base: 'Base',
};

export const LOOK_LABELS: Record<Look, string> = {
  classic: 'Classic',
  handDrawn: 'Hand-drawn',
};

export function isDiagramTheme(value: string): value is DiagramTheme {
  return (DIAGRAM_THEMES as readonly string[]).includes(value);
}

export function isLook(value: string): value is Look {
  return (LOOKS as readonly string[]).includes(value);
}

interface MermaidRenderConfig {
  diagramTheme: DiagramTheme;
  look: Look;
}

let currentConfig: MermaidRenderConfig = { diagramTheme: 'default', look: 'classic' };
let mermaidPromise: ReturnType<typeof loadMermaid> | null = null;
let zenumlPromise: Promise<void> | null = null;

function buildInitOptions(config: MermaidRenderConfig) {
  return {
    startOnLoad: false,
    securityLevel: 'strict' as const,
    htmlLabels: false,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    theme: config.diagramTheme,
    look: config.look,
  };
}

async function loadMermaid() {
  const [{ default: mermaid }, { default: elkLayouts }] = await Promise.all([
    import('mermaid'),
    import('@mermaid-js/layout-elk'),
  ]);
  mermaid.registerLayoutLoaders(elkLayouts);
  mermaid.initialize(buildInitOptions(currentConfig));
  return mermaid;
}

function getMermaid() {
  mermaidPromise ??= loadMermaid();
  return mermaidPromise;
}

/**
 * Records the diagram theme and look, re-initializing Mermaid only when the
 * config actually changed since the last render — a no-op call (e.g. the
 * default at first mount) never forces a dynamic import of Mermaid on its
 * own. Callers force a re-render afterward (App.tsx): Mermaid's global
 * config only takes effect on the next `render()` call, it doesn't
 * retroactively redraw anything already on the sheet.
 */
export async function setMermaidConfig(diagramTheme: DiagramTheme, look: Look): Promise<void> {
  if (currentConfig.diagramTheme === diagramTheme && currentConfig.look === look) return;
  currentConfig = { diagramTheme, look };
  const mermaid = await getMermaid();
  mermaid.initialize(buildInitOptions(currentConfig));
}

function ensureZenuml(mermaid: Mermaid) {
  zenumlPromise ??= (async () => {
    const { default: zenuml } = await import('@mermaid-js/mermaid-zenuml');
    await mermaid.registerExternalDiagrams([zenuml]);
  })();
  return zenumlPromise;
}

/**
 * SPEC.md §3: "Mermaid render leaks a DOM node on parse failure." A failed
 * `mermaid.render(id, src)` can leave an orphaned `#<id>` element appended to
 * `document.body`; without removing it here the DOM grows unbounded over a
 * long typing session.
 */
export const renderMermaid: RenderFn = async (id, source, onNotice) => {
  const mermaid = await getMermaid();

  if (detectDiagramType(source)?.id === 'zenuml') {
    if (zenumlPromise === null) onNotice(ZENUML_LOADING_NOTICE);
    await ensureZenuml(mermaid);
  }

  try {
    const result = await mermaid.render(id, source);
    // svgDimensions.ts's own doc comment has the full reasoning: mermaid's
    // raw output relies on the browser's own auto-sizing (width: 100%, no
    // height attribute), which is exactly the ambiguity SPEC.md §6 gotcha 3
    // already rules out for the export path — this is the same fix, applied
    // here so every consumer (the live preview, the error card, export's own
    // input) gets a self-contained, deterministically-sized SVG.
    return { svg: withExplicitDimensions(result.svg) };
  } catch (err) {
    document.getElementById(id)?.remove();
    throw err;
  }
};
